"use client"

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import type { SQLNamespace } from "@codemirror/lang-sql"

import type { ColumnEnums, ColumnTypes } from "./SqlSchemaPanel"

interface SchemaPanelContent {
  schema: SQLNamespace
  columnEnums?: ColumnEnums
  /** SQL type per column, keyed by table then column. */
  columnTypes?: ColumnTypes
  /** Whether the schema is still being fetched (drives the panel's loading state). */
  isLoading?: boolean
  /** Re-runs the schema fetch; surfaced as a retry control when the schema is empty. */
  onRetry?: () => void
}

interface SqlSchemaPanelValue {
  /** The schema to show, registered by the active SQL config (null when none). */
  content: SchemaPanelContent | null
  /** Whether the reference panel is currently shown. */
  isOpen: boolean
  /** Registers (or clears) the schema the panel renders. */
  setContent: (content: SchemaPanelContent | null) => void
  /** Toggles the panel open/closed. */
  toggle: () => void
  /** Closes the panel. */
  close: () => void
}

const SqlSchemaPanelContext = createContext<SqlSchemaPanelValue | null>(null)

/**
 * Coordinates the SQL schema reference panel: a SQL editor's config registers
 * its schema and toggles visibility from the editor footer, while a layout
 * higher up renders the panel where there's room (e.g. a gutter beside a form).
 */
export function SqlSchemaPanelProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SchemaPanelContent | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const toggle = useCallback(() => setIsOpen((open) => !open), [])
  const close = useCallback(() => setIsOpen(false), [])
  const value = useMemo(
    () => ({ content, isOpen, setContent, toggle, close }),
    [content, isOpen, toggle, close],
  )
  return (
    <SqlSchemaPanelContext.Provider value={value}>
      {children}
    </SqlSchemaPanelContext.Provider>
  )
}

export function useSqlSchemaPanel(): SqlSchemaPanelValue {
  const context = useContext(SqlSchemaPanelContext)
  if (context === null) {
    throw new Error(
      "useSqlSchemaPanel must be used within a SqlSchemaPanelProvider",
    )
  }
  return context
}
